class GorisHeaderSearch extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.input = this.querySelector('input[name="q"]');
    this.panel = this.querySelector('[data-search-panel]');
    this.results = this.querySelector('[data-search-options]');
    this.status = this.querySelector('[role="status"]');
    this.viewAll = this.querySelector('[data-search-all]');
    this.activeIndex = -1;
    const options = { signal: this.controller.signal };
    this.input.addEventListener('input', (event) => {
      if (!event.isComposing) this.schedule();
    }, options);
    this.input.addEventListener('compositionend', () => this.schedule(), options);
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim()) this.schedule();
    }, options);
    this.input.addEventListener('keydown', (event) => this.onKeyDown(event), options);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.panel.hidden) {
        event.preventDefault();
        this.input.focus();
        this.close();
      }
    }, options);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.close();
    }, options);
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.close();
    }, options);
  }

  disconnectedCallback() {
    this.controller.abort();
    this.request?.abort();
    clearTimeout(this.timer);
  }

  schedule() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.results.replaceChildren();
    this.setActive(-1);
    const term = this.input.value.trim();
    if (!term) {
      this.close();
      return;
    }
    const url = new URL(this.querySelector('form').action);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    this.viewAll.href = url.toString();
    this.status.textContent = this.dataset.loading;
    this.open();
    this.timer = setTimeout(() => this.fetchResults(term), 200);
  }

  async fetchResults(term) {
    const request = new AbortController();
    this.request = request;
    const url = new URL(this.dataset.predictiveUrl, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', 'goris-header-predictive');
    try {
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error('Search unavailable');
      const html = await response.text();
      if (request.signal.aborted || this.input.value.trim() !== term) return;
      const document = new DOMParser().parseFromString(html, 'text/html');
      const content = document.querySelector('[data-header-search-response]');
      if (!content) throw new Error('Search response unavailable');
      this.results.replaceChildren(...content.children);
      this.results.querySelectorAll('[role="option"]').forEach((item, index) => {
        item.id = `${this.results.id}-option-${index}`;
        item.setAttribute('aria-selected', 'false');
      });
      this.status.textContent = content.dataset.message;
      this.setActive(-1);
    } catch (error) {
      if (request.signal.aborted) return;
      this.status.textContent = this.dataset.error;
    }
  }

  get options() {
    return Array.from(this.results.querySelectorAll('[role="option"]'));
  }

  setActive(index) {
    this.activeIndex = index;
    this.options.forEach((option, position) => {
      option.setAttribute('aria-selected', String(position === index));
    });
    const selected = this.options[index];
    if (selected) {
      this.input.setAttribute('aria-activedescendant', selected.id);
      selected.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  onKeyDown(event) {
    if (event.isComposing || this.panel.hidden) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.options.length) return;
      event.preventDefault();
      const next = event.key === 'ArrowDown' ? this.activeIndex + 1 : (this.activeIndex < 0 ? this.options.length - 1 : this.activeIndex - 1);
      this.setActive((next + this.options.length) % this.options.length);
    } else if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      this.options[this.activeIndex]?.querySelector('a')?.click();
    }
  }

  open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.setActive(-1);
  }
}

if (!customElements.get('goris-header-search')) {
  customElements.define('goris-header-search', GorisHeaderSearch);
}
